import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { useMantineColorScheme, MultiSelect } from "@mantine/core";
import MetricSelection, { METRIC_GROUPS, ATTRIBUTES_LABELS } from './MetricSelection';
import { getColorForKey, getContrastColor, GROUP_COLORS, colorForGroupIndex } from './chartConstants';
import { useTranslation } from "react-i18next";
import { formatCompactNumber, formatEnUsValue } from "../../utils/helpers";

// COLORS are imported from chartConstants

// processData moved into component to use attributeLabelToKey and getColorForKey

const EXCLUDED_ATTRIBUTES = [
  "Thermal Supply CO2 (kg)",
  "Unmet Energy Off Grid (kWh)",
  "Unmet Energy OnGrid (kWh)",
  "Excess Energy OffGrid (kWh)",
  "Excess Energy Grid Connected (kWh)"
];



const CompareChart = ({ data }) => {
  const ref = useRef();
  const resizeObserverRef = useRef();
  const initializedScenariosRef = useRef(false);
  const { colorScheme } = useMantineColorScheme();
  const [selectedAttributes, setSelectedAttributes] = useState(() => {
    const rates = METRIC_GROUPS?.find(g => g.id === 'rates');
    return rates ? [...rates.keys] : [];
  });
  // MultiSelect state for x-axis (scenarios)
  const [selectedScenarios, setSelectedScenarios] = useState([]);
  const { t } = useTranslation();
  const displayCurrency = 'USD';
  const axisTextColor = colorScheme === "dark" ? "#d5d7e0" : "#495057";

  // Helper: map a data attribute string (label or key) to metric key
  const attributeLabelToKey = (attr) => {
    if (!attr) return null;
    // if attr is already a key
    if (ATTRIBUTES_LABELS[attr]) return attr;
    // try exact match to label (with or without currency replacement)
    const exact = Object.keys(ATTRIBUTES_LABELS).find(k => {
      const lab = ATTRIBUTES_LABELS[k] || '';
      if (lab.replace('{currency}', displayCurrency) === attr) return true;
      if (lab === attr) return true;
      return false;
    });
    if (exact) return exact;
    // try case-insensitive key match
    const ciKey = Object.keys(ATTRIBUTES_LABELS).find(k => k.toLowerCase() === String(attr).toLowerCase());
    if (ciKey) return ciKey;
    // normalize helper: strip currency tokens, symbols and non-alphanumerics
    const normalize = (s) => String(s || '').toLowerCase().replace(/\{currency\}/g, '').replace(/\$|usd|eur|gbp|€|£/g, '').replace(/[^a-z0-9]/g, '');
    const normAttr = normalize(attr);
    // try to match by normalized label
    const normKey = Object.keys(ATTRIBUTES_LABELS).find(k => normalize(ATTRIBUTES_LABELS[k]) === normAttr || normalize(ATTRIBUTES_LABELS[k].replace('{currency}', displayCurrency)) === normAttr);
    if (normKey) return normKey;
    return null;
  };

  // Compute all scenario keys (x-axis labels) from data
  // Map scenario keys to display labels
  const scenarioLabelMap = {
    referenceExistingMicrogrid: t("result.scenario.existing"),
    completeMicrogrid: t("result.scenario.complete")
  };
  const allScenarioKeys = React.useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) return [];
    return Object.keys(data[0]).filter(k => k !== "attribute");
  }, [data]);
  // Helper to get display label
  const getScenarioLabel = k => scenarioLabelMap[k] || k;

  // Initialize selectedScenarios to all scenarios on first load
  useEffect(() => {
    // Populate scenarios only on first load. Do NOT re-populate after user clears selections.
    if (allScenarioKeys.length === 0) return;
    if (!initializedScenariosRef.current) {
      if (!selectedScenarios || selectedScenarios.length === 0) {
        // Default to Reference microgrid (if present) plus one other scenario.
        const preferred = [];
        if (allScenarioKeys.includes('referenceExistingMicrogrid')) preferred.push('referenceExistingMicrogrid');
        // Prefer completeMicrogrid as the second scenario; otherwise pick first non-reference
        if (allScenarioKeys.includes('completeMicrogrid')) {
          if (!preferred.includes('completeMicrogrid')) preferred.push('completeMicrogrid');
        } else {
          const other = allScenarioKeys.find(k => k !== 'referenceExistingMicrogrid');
          if (other && !preferred.includes(other)) preferred.push(other);
        }
        // fallback: if nothing matched, pick the first available
        if (preferred.length === 0 && allScenarioKeys.length > 0) preferred.push(allScenarioKeys[0]);
        setSelectedScenarios(preferred);
      }
      initializedScenariosRef.current = true;
    }
    // eslint-disable-next-line
  }, [allScenarioKeys]);

  // Initialize selectedAttributes to all available attributes (excluding global excludes)
  useEffect(() => {
    if ((!selectedAttributes || selectedAttributes.length === 0) && Array.isArray(data) && data.length > 0) {
      const allData = data.filter(d => !EXCLUDED_ATTRIBUTES.includes(d.attribute));
      // normalize to metric keys when possible
      setSelectedAttributes(allData.map(d => attributeLabelToKey(d.attribute) || d.attribute));
    }
    // eslint-disable-next-line
  }, [data]);

  useEffect(() => {
    function draw() {
      if (!data || !Array.isArray(data) || data.length === 0 || !ref.current) return;
      // Separate full attribute list (excluding globally excluded ones) from visible subset.
      const allData = data.filter(d => !EXCLUDED_ATTRIBUTES.includes(d.attribute));
      // visibleData: map each data attribute to its metric key and include if selectedAttributes contains that key
      // normalize selectedAttributes to keys for reliable matching (supports both keys and labels)
      const normalizedSelected = new Set((selectedAttributes || []).map(sa => attributeLabelToKey(sa) || sa));
      const visibleData = allData.filter(d => {
        const key = attributeLabelToKey(d.attribute) || d.attribute;
        return normalizedSelected.has(key) || normalizedSelected.has(d.attribute);
      });
      // Debug logs to help identify why negatives may not render
      try {
        console.log('CompareChart: normalizedSelected=', Array.from(normalizedSelected));
        console.log('CompareChart: visibleData count=', visibleData.length, 'attributes=', visibleData.map(v=>v.attribute));
        const sampleNPC = visibleData.find(v => (attributeLabelToKey(v.attribute) || v.attribute) === 'NPC' || String(v.attribute).toLowerCase().includes('net present cost'));
        const sampleOp = visibleData.find(v => (attributeLabelToKey(v.attribute) || v.attribute) === 'OpCost' || String(v.attribute).toLowerCase().includes('operation cost'));
        if (sampleNPC) console.log('CompareChart: NPC sample row=', sampleNPC);
        if (sampleOp) console.log('CompareChart: OpCost sample row=', sampleOp);
      } catch (e) { console.warn('CompareChart debug log failed', e); }
      const scenarioKeys = Array.isArray(selectedScenarios) ? selectedScenarios : [];
      const isEmptyScenarioSelection = !scenarioKeys || scenarioKeys.length === 0;
      // Build grouped data only from visible attributes so bars adjust when toggled
      const processData = (rows) => {
        if (!rows || !Array.isArray(rows) || rows.length === 0) return { chartData: [], scenarioKeys: [], attributes: [] };
        const scenarioKeysLocal = Object.keys(rows[0] || {}).filter(k => k !== 'attribute');
        const attributesLocal = rows.map(r => r.attribute);
        const chartDataLocal = scenarioKeysLocal.map((scenario) => {
          const stacks = rows.map((row) => {
            let value = row ? row[scenario] : 0;
            if (typeof value === 'string') {
              value = value === 'Infinity' || value === 'NaN' ? 0 : Number(String(value).replace(/[^\d.-]/g, ''));
            }
            if (isNaN(value)) value = 0;
            const key = attributeLabelToKey(row?.attribute) || row?.attribute || '';
            const group = METRIC_GROUPS.find(g => g.keys.includes(key));
            const idxInGroup = group ? group.keys.indexOf(key) : -1;
            const color = idxInGroup >= 0 ? colorForGroupIndex(idxInGroup) : getColorForKey(key);
            return { attribute: row?.attribute || '', value, color, key };
          });
          return { scenario, stacks };
        });
        return { chartData: chartDataLocal, scenarioKeys: scenarioKeysLocal, attributes: attributesLocal };
      };

      const { chartData } = (() => {
        const base = processData(visibleData);
        console.log('CompareChart: processData result sample, scenario count=', base.chartData.length);
        const filteredChartData = base.chartData.filter(d => scenarioKeys.includes(d.scenario));
        filteredChartData.forEach(d => { d.displayScenario = getScenarioLabel(d.scenario); });
        return { chartData: filteredChartData };
      })();
      // log min/max before scales
      try {
        const allValues = chartData.flatMap(d => d.stacks.map(s => s.value));
        console.log('CompareChart: chartData attributes sample count=', chartData.length, 'valuesCount=', allValues.length);
        if (allValues.length > 0) console.log('CompareChart: values min/max=', Math.min(...allValues), Math.max(...allValues));
      } catch (e) { console.warn('CompareChart value log failed', e); }
      // Legend should list all (non-excluded) attributes regardless of visibility
      const attributes = allData.map(d => d.attribute);
      const shownChartData = chartData;
      const shownScenarioKeys = scenarioKeys;
          const containerWidth = ref.current.clientWidth || 900;
          const width = containerWidth;
          const height = 520;
          const margin = { top: 40, right: 40, bottom: 80, left: 120 };
      const innerW = width - margin.left - margin.right;
      const innerH = height - margin.top - margin.bottom;

      // Color tokens for Mantine dark/light
      const axisTextColor = colorScheme === "dark" ? "#d5d7e0" : "#495057";
      const legendTextColor = colorScheme === "dark" ? "#d5d7e0" : "#495057";
      const tooltipBg = colorScheme === "dark" ? "#222832" : "#fff";
      const tooltipText = colorScheme === "dark" ? "#d5d7e0" : "#222";
      const tooltipBorder = colorScheme === "dark" ? "#555" : "#888";
      const svgBg = colorScheme === "dark" ? "#111111" : "#ffffff";
      const zeroLineColor = "#888";

      // Remove previous svg
      d3.select(ref.current).selectAll("svg").remove();
      const svg = d3.select(ref.current)
        .append("svg")
        .attr("width", width)
        .attr("height", height)
        .style("font-family", "Nunito, sans-serif")
        .style("background", svgBg);

      const dark = colorScheme === 'dark';

      // Toolbar — identical pattern to TimeSeriesGraph
      d3.select(ref.current).selectAll('.compare-chart-toolbar').remove();
      const toolbar = d3.select(ref.current).append('div').attr('class', 'compare-chart-toolbar')
        .style('position', 'absolute')
        .style('top', '4px')
        .style('right', '8px')
        .style('display', 'flex')
        .style('gap', '4px')
        .style('font-family', 'Nunito, sans-serif')
        .style('z-index', 30)
        .style('pointer-events', 'auto');

      let zoomBehavior = null;

      const mkBtn = (label, title) => toolbar.append('button')
        .text(label)
        .attr('type', 'button')
        .attr('title', title)
        .style('padding', '2px 6px')
        .style('font-size', '12px')
        .style('font-family', 'Nunito, sans-serif')
        .style('cursor', 'pointer')
        .style('border', '1px solid #777')
        .style('background', dark ? '#222' : '#fafafa')
        .style('color', dark ? '#eee' : '#222')
        .style('border-radius', '3px')
        .style('display', 'flex')
        .style('align-items', 'center')
        .style('justify-content', 'center');

      const zoomInBtn  = mkBtn('+',      t('timeSeries.toolbar.zoomIn')  || 'Zoom in');
      const zoomOutBtn = mkBtn('-',      t('timeSeries.toolbar.zoomOut') || 'Zoom out');
      const resetBtn   = mkBtn(t('timeSeries.toolbar.reset') || 'Reset', t('timeSeries.toolbar.reset') || 'Reset zoom');

      // Download <select> dropdown (matches TimeSeriesGraph exactly)
      const downloadSelect = toolbar.append('select')
        .attr('title', t('timeSeries.toolbar.download') || 'Download')
        .style('padding', '2px 6px')
        .style('font-size', '12px')
        .style('font-family', 'Nunito, sans-serif')
        .style('cursor', 'pointer')
        .style('border', '1px solid #777')
        .style('background', dark ? '#222' : '#fafafa')
        .style('color', dark ? '#eee' : '#222')
        .style('border-radius', '3px');
      downloadSelect.append('option').attr('value', '').attr('disabled', true).attr('selected', true).attr('hidden', true).text(t('timeSeries.toolbar.download') || '⬇ Download');
      downloadSelect.append('option').attr('value', 'png').text('PNG');
      downloadSelect.append('option').attr('value', 'csv').text('CSV');

      // PNG export (mirrors TimeSeriesGraph downloadPNG)
      const downloadPNG = () => {
        try {
          const svgEl = svg.node();
          if (!svgEl) return;
          const cloned = svgEl.cloneNode(true);
          const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
          bg.setAttribute('x', '0'); bg.setAttribute('y', '0');
          bg.setAttribute('width', svgEl.getAttribute('width'));
          bg.setAttribute('height', svgEl.getAttribute('height'));
          bg.setAttribute('fill', dark ? '#111111' : '#ffffff');
          cloned.insertBefore(bg, cloned.firstChild);
          const serializer = new XMLSerializer();
          let svgString = serializer.serializeToString(cloned);
          if (!svgString.match(/^<svg[^>]+xmlns=/)) svgString = svgString.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
          const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
          const url = URL.createObjectURL(svgBlob);
          const img = new Image();
          img.onload = () => {
            try {
              const canvas = document.createElement('canvas');
              canvas.width = img.width; canvas.height = img.height;
              const ctx = canvas.getContext('2d');
              ctx.fillStyle = dark ? '#111111' : '#ffffff';
              ctx.fillRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(img, 0, 0);
              URL.revokeObjectURL(url);
              const pngUrl = canvas.toDataURL('image/png');
              const a = document.createElement('a');
              const ts = new Date().toISOString().replace(/[:.]/g, '-');
              a.download = `compare-chart-${ts}.png`;
              a.href = pngUrl;
              document.body.appendChild(a); a.click(); document.body.removeChild(a);
            } catch (e) { URL.revokeObjectURL(url); }
          };
          img.onerror = () => URL.revokeObjectURL(url);
          img.src = url;
        } catch (e) {}
      };

      // CSV export — pivot: rows = attributes, columns = scenarios
      const downloadCSV = () => {
        try {
          const sKeys = Array.isArray(selectedScenarios) ? selectedScenarios : [];
          const attrs = Array.isArray(selectedAttributes) ? selectedAttributes : [];
          if (sKeys.length === 0 || attrs.length === 0) return;

          const sanitize = (v) => {
            if (v === null || v === undefined) return '';
            if (typeof v === 'number') return isFinite(v) ? v : '';
            const s = String(v).trim();
            if (s === 'Infinity' || s === 'NaN' || s === 'undefined' || s === 'null') return '';
            // strip non-numeric characters but keep minus and dot
            const num = s.replace(/[^\d.-]/g, '');
            return num !== '' ? num : s;
          };

          // Header: Attribute, Scenario1, Scenario2, ...
          const header = ['Attribute', ...sKeys.map(sk => getScenarioLabel(sk))];
          const rows = [header];

          const allDataLocal = data.filter(d => !EXCLUDED_ATTRIBUTES.includes(d.attribute));
          allDataLocal.forEach((r) => {
            const key = attributeLabelToKey(r.attribute) || r.attribute;
            if (!attrs.includes(key) && !attrs.includes(r.attribute)) return;
            const row = [r.attribute, ...sKeys.map(sk => sanitize(r[sk]))];
            rows.push(row);
          });

          const csvContent = rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.setAttribute('download', 'compare-chart.csv');
          document.body.appendChild(link); link.click(); document.body.removeChild(link);
        } catch (e) {}
      };

      downloadSelect.on('change', function () {
        const val = this.value;
        this.value = '';
        if (val === 'png') downloadPNG();
        else if (val === 'csv') downloadCSV();
      });

      const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);
      const plotArea = g.append("g").attr("class", "plot-area");

      // ClipPath so bars don't overflow plot boundary during zoom/pan
      const clipId = 'compare-bars-clip';
      svg.append('defs').append('clipPath').attr('id', clipId)
        .append('rect').attr('x', 0).attr('y', 0)
        .attr('width', innerW).attr('height', innerH);

      // X scale: scenarios (use display labels)
      const x = d3.scaleBand()
        .domain(shownChartData.map(d => d.displayScenario))
        .range([0, innerW])
        .padding(0.2);

      // Y scale: include negative values if present
      const maxY = shownChartData.length ? d3.max(shownChartData, d => d3.max(d.stacks, s => s.value)) : 0;
      const minY = shownChartData.length ? d3.min(shownChartData, d => d3.min(d.stacks, s => s.value)) : 0;
      const y = d3.scaleLinear()
        .domain([Math.min(0, minY), Math.max(1, maxY)])
        .nice()
        .range([innerH, 0]);

      // Grid line color (subtle, adapts to theme)
      const gridColor = colorScheme === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.06)";

      // Horizontal grid lines (use y ticks)
      plotArea.append("g")
        .attr("class", "grid-y")
        .call(d3.axisLeft(y).ticks(8).tickSize(-innerW).tickFormat(() => ""))
        .selectAll("line")
        .attr("stroke", gridColor)
        .attr("stroke-width", 1)
        .attr("shape-rendering", "crispEdges");
      plotArea.select(".grid-y").select(".domain").remove();


      // Draw axes

      // Draw x-axis without the domain line, using display labels
      const xAxis = plotArea.append("g")
        .attr("class", "x-axis")
        .attr("transform", `translate(0,${innerH})`)
        .call(d3.axisBottom(x));
      // Rotate x-axis labels only when there are many scenarios; otherwise keep horizontal
      const rotateThreshold = 6;
      const rotateAngle = shownChartData.length > rotateThreshold ? -30 : 0;
      xAxis.selectAll("text")
        .attr("font-size", 12)
        .attr("fill", axisTextColor)
        .attr("font-family", "Nunito, sans-serif")
        .each(function() {
          const txt = d3.select(this);
          if (rotateAngle === 0) {
            txt.attr("transform", null).attr("text-anchor", "middle");
          } else {
            txt.attr("transform", `rotate(${rotateAngle})`).attr("text-anchor", "end");
          }
        });
      xAxis.select(".domain").attr("stroke", "none");

      // Vertical grid lines at scenario centers
      if (shownChartData && shownChartData.length > 0) {
        const xCenters = shownChartData.map(d => x(d.displayScenario) + x.bandwidth() / 2);
        plotArea.append("g")
          .attr("class", "grid-x")
          .selectAll("line")
          .data(xCenters)
          .enter()
          .append("line")
          .attr("x1", d => d)
          .attr("x2", d => d)
          .attr("y1", 0)
          .attr("y2", innerH)
          .attr("stroke", gridColor)
          .attr("stroke-width", 1)
          .attr("shape-rendering", "crispEdges");
      }

      const yAxisG = plotArea.append("g").attr("class", "y-axis")
        .call(d3.axisLeft(y).ticks(8).tickFormat(formatCompactNumber));
      yAxisG.selectAll("text")
        .attr("font-size", 12)
        .attr("fill", axisTextColor)
        .attr("font-family", "Nunito, sans-serif");

      // Axis labels
      g.append("text")
        .attr("x", innerW / 2)
        .attr("y", innerH + 60)
        .attr("text-anchor", "middle")
        .attr("fill", axisTextColor)
        .attr("font-size", 14)
        .attr("font-family", "Nunito, sans-serif")
        .text(t("result.compare.axis.scenario"));

      // Determine Y-axis unit from selectedAttributes (first selected key)
      const firstSelectedKey = Array.isArray(selectedAttributes) && selectedAttributes.length > 0 ? selectedAttributes[0] : null;
      const selectedGroup = METRIC_GROUPS.find(g => g.keys.includes(firstSelectedKey));
      const unit = selectedGroup ? (selectedGroup.id === 'rates' ? '%' : selectedGroup.id === 'unit_costs' ? `${displayCurrency}/kWh` : selectedGroup.id === 'time' ? 'Years' : selectedGroup.id === 'energy' ? 'kWh' : selectedGroup.id === 'emissions' ? 'kg CO2' : displayCurrency) : displayCurrency;

      g.append("text")
        .attr("transform", "rotate(-90)")
        .attr("x", -innerH / 2)
        .attr("y", -70)
        .attr("text-anchor", "middle")
        .attr("fill", axisTextColor)
        .attr("font-size", 14)
        .attr("font-family", "Nunito, sans-serif")
        .text(`${t("result.compare.axis.value")} ${unit ? `(${unit})` : ''}`);

      // Zero line
      const zeroLineG = g.append("line").attr("class", "zero-line")
        .attr("x1", 0).attr("x2", innerW)
        .attr("stroke", zeroLineColor).attr("stroke-width", 1)
        .attr("display", y(0) >= 0 && y(0) <= innerH ? null : "none")
        .attr("y1", y(0)).attr("y2", y(0));

      // If there are no scenarios selected, show a centered message and leave axes empty
      if (isEmptyScenarioSelection) {
        plotArea.append("text")
          .attr("x", innerW / 2)
          .attr("y", innerH / 2)
          .attr("text-anchor", "middle")
          .attr("fill", axisTextColor)
          .attr("font-size", 14)
          .attr("font-family", "Nunito, sans-serif")
          .text(t('result.compare.noScenarios') || 'No scenarios selected');
        return;
      }

      // Tooltip
      let tooltip = d3.select(ref.current).select(".d3-tooltip");
      if (tooltip.empty()) {
        tooltip = d3.select(ref.current)
          .append("div")
          .attr("class", "d3-tooltip")
          .style("position", "absolute")
          .style("pointer-events", "none")
          .style("background", tooltipBg)
          .style("color", tooltipText)
          .style("border", `1px solid ${tooltipBorder}`)
          .style("padding", "6px 10px")
          .style("font-size", "13px")
          .style("font-family", "Nunito, sans-serif")
          .style("border-radius", "4px")
          .style("box-shadow", "0 2px 8px rgba(0,0,0,.15)")
          .style("display", "none");
      } else {
        tooltip.style("background", tooltipBg)
          .style("color", tooltipText)
          .style("border", `1px solid ${tooltipBorder}`);
      }

      // Grouped (side-by-side) bars: create inner band for attributes within each scenario
      const visibleAttributes = (visibleData && visibleData.length > 0) ? visibleData.map(d => d.attribute) : [''];
      const innerBand = d3.scaleBand()
        .domain(visibleAttributes)
        .range([0, x.bandwidth()])
        .padding(0.1);
      const barMinWidth = Math.max(1, innerBand.bandwidth());

      // Bars live inside a clipped container so they don't overflow the axes
      const barsArea = plotArea.append('g').attr('clip-path', `url(#${clipId})`);
      barsArea.selectAll(".bar-group")
        .data(shownChartData)
        .enter()
        .append("g")
        .attr("class", "bar-group")
        .attr("transform", d => `translate(${x(d.displayScenario)},0)`)
        .selectAll("rect")
        .data(d => d.stacks)
        .enter()
        .append("rect")
        .attr("x", d => innerBand(d.attribute) ?? 0)
        .attr("width", d => barMinWidth)
        .attr("y", d => y(Math.max(0, d.value)))
        .attr("height", d => Math.abs(y(d.value) - y(0)))
        .attr("fill", d => d.color || getColorForKey(d.key || d.attribute))
        .on("mousemove", function (event, d) {
          const formattedValue = formatEnUsValue(d.value);
          const swatch = d.color || getColorForKey(d.key || d.attribute);
          // determine parent group's scenario to show scenario name
          const parent = d3.select(this.parentNode).datum();
          const scenarioKey = parent && parent.scenario;
          const scenarioLabel = getScenarioLabel(scenarioKey);
          tooltip.style("display", "block")
            .html(`<div style=\"font-weight:700; margin-bottom:6px;\">${scenarioLabel}</div><div style=\"display:flex;align-items:center;\"><span style=\"display:inline-block;width:12px;height:12px;background:${swatch};border-radius:2px;margin-right:7px;\"></span><b>${d.attribute}</b></div><div style=\"margin-top:6px;\">${t("result.compare.tooltip.value")}: ${formattedValue}</div>`);
          const bounds = ref.current.getBoundingClientRect();
          tooltip.style("left", (event.clientX - bounds.left + 12) + "px")
            .style("top", (event.clientY - bounds.top - 8) + "px");
          // Removed hover stroke to avoid large border occupying space
        })
        .on("mouseleave", function () {
          tooltip.style("display", "none");
          d3.select(this).attr("stroke", null).attr("stroke-width", null);
        });

      // Zoom on Y axis — rescaleY works on linear scales
      const zoom = d3.zoom()
        .scaleExtent([1, 20])
        .on('zoom', (event) => {
          const newY = event.transform.rescaleY(y);
          // Update bar positions and heights
          barsArea.selectAll('.bar-group rect')
            .attr('y', d => newY(Math.max(0, d.value)))
            .attr('height', d => Math.abs(newY(d.value) - newY(0)));
          // Redraw Y axis
          yAxisG.call(d3.axisLeft(newY).ticks(8).tickFormat(formatCompactNumber));
          yAxisG.selectAll('text').attr('font-size', 12).attr('fill', axisTextColor).attr('font-family', 'Nunito, sans-serif');
          // Redraw horizontal grid lines
          plotArea.select('.grid-y')
            .call(d3.axisLeft(newY).ticks(8).tickSize(-innerW).tickFormat(() => ''))
            .selectAll('line').attr('stroke', gridColor).attr('stroke-width', 1).attr('shape-rendering', 'crispEdges');
          plotArea.select('.grid-y').select('.domain').remove();
          // Update zero line
          const z = newY(0);
          zeroLineG
            .attr('display', z >= 0 && z <= innerH ? null : 'none')
            .attr('y1', z).attr('y2', z);
        });
      svg.call(zoom);
      zoomBehavior = zoom;

      // Wire zoom buttons
      zoomInBtn.on('click',  () => svg.transition().duration(300).call(zoomBehavior.scaleBy, 1.5));
      zoomOutBtn.on('click', () => svg.transition().duration(300).call(zoomBehavior.scaleBy, 1 / 1.5));
      resetBtn.on('click',   () => svg.transition().duration(300).call(zoomBehavior.transform, d3.zoomIdentity));

      // Legend removed (colors shown next to metrics in MetricSelection)
    }

    draw();

    if (!resizeObserverRef.current && ref.current) {
      resizeObserverRef.current = new window.ResizeObserver(() => draw());
      resizeObserverRef.current.observe(ref.current);
    }
    return () => {
      if (resizeObserverRef.current) {
        if (ref.current) resizeObserverRef.current.unobserve(ref.current);
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, [data, colorScheme, selectedAttributes, selectedScenarios, allScenarioKeys]);

  return (
    <div style={{ display: "flex", flexDirection: "row", alignItems: "flex-start", width: "100%", minHeight: 540, position: "relative", zoom: "118%" }}>
      <div style={{ width: '80%', minHeight: 540, position: 'relative' }}>
        <div style={{ width: '100%', marginBottom: 8, display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 8 }}>
          {selectedAttributes && selectedAttributes.length > 0 ? (
            selectedAttributes.map(key => {
              const groupObj = METRIC_GROUPS.find(g => g.keys.includes(key));
              const idx = groupObj ? groupObj.keys.indexOf(key) : -1;
              const color = idx >= 0 ? colorForGroupIndex(idx) : getColorForKey(key);
              const label = (ATTRIBUTES_LABELS[key] || key).replace('{currency}', displayCurrency);
              return (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 6px' }}>
                  <div style={{ width: 12, height: 12, background: color, borderRadius: 2 }} />
                  <div style={{ fontWeight: 700, fontSize: 12, color: axisTextColor }}>{label}</div>
                  <div style={{ marginLeft: 6, cursor: 'pointer', color: axisTextColor }} onClick={() => setSelectedAttributes(selectedAttributes.filter(s => s !== key))}>✕</div>
                </div>
              );
            })
          ) : (
            <div style={{ color: '#6c757d' }}>{t('result.compare.noSelection')}</div>
          )}
        </div>
        <div ref={ref} style={{ minHeight: 540, position: 'relative' }} />
      </div>
      <div style={{ width: '20%', marginLeft: 24, marginTop: 10, display: 'flex', flexDirection: 'column', minHeight: 540 }}>
        <div style={{ marginBottom: 12 }}>
          <MetricSelection label={t('result.compare.select.metrics')} value={selectedAttributes} onChange={setSelectedAttributes} currency="USD" showSelectedInTrigger={false} />
        </div>
        
        <div style={{ marginTop: 12 }}>
          <MultiSelect
            data={allScenarioKeys.map(k => ({ value: k, label: getScenarioLabel(k) }))}
            value={selectedScenarios}
            onChange={setSelectedScenarios}
            label={t('result.compare.select.label')}
            placeholder={t('result.compare.select.placeholder')}
            nothingFound={t('result.compare.select.nothingFound')}
            searchable
            clearable
          />
        </div>
      </div>
    </div>
  );
};


export default CompareChart;
